import React, { forwardRef, Ref } from "react";
import styled from "@emotion/styled";
import * as tokens from "../../design-tokens";

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  &.disabled {
    opacity: 0.3;
  }
`;
const StyledLabel = styled.label`
  margin-bottom: 8px;
  font-family: ${tokens.FONT_FAMILIES_ROOBERT}, sans-serif;
  font-size: 20px;
  line-height: 20px;
  color: ${tokens.TINTS_OFF_WHITE_30};
`;
const StyledTextarea = styled.textarea`
  padding: 18px 16px;
  border: 1px solid ${tokens.BORDER_DARK};
  border-radius: 8px;
  font-family: ${tokens.FONT_FAMILIES_ROOBERT}, sans-serif;
  font-size: 16px;
  color: ${tokens.TINTS_OFF_WHITE_20};
  width: auto;
  outline: none;
  &:focus {
    border-color: ${tokens.BRAND_CHARGEBLUE};
  }
  &:active {
    border-color: ${tokens.BRAND_HEROBLUE};
    color: #000000;
  }
  &:disabled {
    cursor: not-allowed;
  }
  &.sr-error {
    border-color: ${tokens.ERROR_DARK};
    color: #6f6d68;
    &:focus {
      border-color: ${tokens.ERROR_DARK};
    }
  }
`;
const StyledHelperText = styled.span`
  font-family: ${tokens.FONT_FAMILIES_ROOBERT}, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${tokens.ERROR_DARK};
`;

type TextareaProps = React.ComponentPropsWithoutRef<"textarea"> & {
  label?: string;
  helperText?: string;
  isError?: boolean;
};
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className = "",
      label,
      helperText,
      isError,
      ...otherProps
    }: TextareaProps,
    ref: Ref<HTMLTextAreaElement>
  ) => {
    const textareaId = `textarea-${label}`;

    return (
      <StyledContainer
        className={`${className} ${otherProps.disabled ? "disabled" : ""}`}
      >
        {label && <StyledLabel htmlFor={textareaId}>{label}</StyledLabel>}
        <StyledTextarea
          ref={ref}
          id={textareaId}
          className={`${isError ? "sr-error" : ""}`}
          placeholder={otherProps.placeholder || label}
          disabled={otherProps.disabled}
          {...(isError ? { "aria-invalid": true } : {})}
          {...otherProps}
        />

        {helperText && isError && (
          <StyledHelperText role="alert">{helperText}</StyledHelperText>
        )}
      </StyledContainer>
    );
  }
);

export { Textarea };
export type { TextareaProps };
